import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Alert,
  LinearProgress,
  Paper,
  IconButton,
  FormControlLabel,
  Switch,
  Snackbar,
} from '@mui/material';
import {
  CloudUpload,
  Close,
  InsertDriveFile,
  CheckCircle,
  AutoFixHigh,
} from '@mui/icons-material';
import { externalDocumentsService } from '../services/api';
import { createWorker } from 'tesseract.js';

const CATEGORIES = [
  'Fournitures bureau',
  'Matériel informatique',
  'Frais véhicule',
  'Repas/Restaurant',
  'Hébergement',
  'Télécom/Internet',
  'Assurances',
  'Banque/Frais financiers',
  'Formation',
  'Logiciels/Outils',
  'Maintenance/Réparation',
  'Marketing/Publicité',
  'Électricité/Eau',
  'Loyer',
  'Autres charges',
];

const PAYMENT_METHODS = [
  'Virement',
  'Carte bancaire',
  'Chèque',
  'Espèces',
  'Prélèvement',
];

function UploadDocumentDialog({ open, onClose, onSuccess }) {
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [ocrProgress, setOcrProgress] = useState(0);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [formData, setFormData] = useState({
    supplierName: '',
    category: '',
    description: '',
    amount: '',
    vatRate: '20',
    documentDate: new Date().toISOString().split('T')[0],
    dueDate: '',
    paymentMethod: 'Virement',
    isPaid: false,
    paymentDate: '',
    tags: '',
    notes: '',
  });

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  function handleFile(selectedFile) {
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    const maxSize = 10 * 1024 * 1024; // 10 MB

    if (!allowedTypes.includes(selectedFile.type)) {
      setError('Type de fichier non autorisé. Formats acceptés: PDF, JPG, PNG');
      return;
    }

    if (selectedFile.size > maxSize) {
      setError('Fichier trop volumineux. Taille max: 10 MB');
      return;
    }

    setFile(selectedFile);
    setError(null);

    // Lancer l'OCR automatiquement pour les images
    if (selectedFile.type.startsWith('image/')) {
      extractTextFromImage(selectedFile);
    }
  }

  const extractTextFromImage = async (imageFile) => {
    try {
      setProcessing(true);
      setOcrProgress(0);
      setSnackbar({ open: true, message: '🔍 Extraction des données en cours...', severity: 'info' });

      const worker = await createWorker('fra', 1, {
        logger: (m) => {
          if (m.status === 'recognizing text') {
            setOcrProgress(Math.round(m.progress * 100));
          }
        },
      });

      const { data: { text } } = await worker.recognize(imageFile);
      await worker.terminate();

      // Parser le texte pour extraire les infos
      parseInvoiceText(text);

      setSnackbar({ open: true, message: '✅ Données extraites ! Vérifiez et corrigez si nécessaire.', severity: 'success' });
    } catch (err) {
      console.error('Erreur OCR:', err);
      setSnackbar({ open: true, message: '⚠️ Extraction automatique échouée. Remplissez manuellement.', severity: 'warning' });
    } finally {
      setProcessing(false);
      setOcrProgress(0);
    }
  };

  const parseInvoiceText = (text) => {
    const updates = { ...formData };

    // Extraire les montants (formats: 123.45€, 123,45 €, €123.45)
    const amountRegex = /(?:€\s?)?(\d{1,6}[.,]\d{2})(?:\s?€)?/g;
    const amounts = [];
    let match;
    while ((match = amountRegex.exec(text)) !== null) {
      const amount = parseFloat(match[1].replace(',', '.'));
      if (amount > 0 && amount < 1000000) {
        amounts.push(amount);
      }
    }
    if (amounts.length > 0) {
      updates.amount = Math.max(...amounts).toFixed(2);
    }

    // Extraire les dates (formats: DD/MM/YYYY, DD-MM-YYYY, YYYY-MM-DD)
    const dateRegex = /(\d{1,2}[/-]\d{1,2}[/-]\d{4})|(\d{4}[/-]\d{1,2}[/-]\d{1,2})/g;
    const dates = [];
    while ((match = dateRegex.exec(text)) !== null) {
      const dateStr = match[0];
      const parsedDate = parseDate(dateStr);
      if (parsedDate) {
        dates.push(parsedDate);
      }
    }
    if (dates.length > 0) {
      updates.documentDate = dates[0];
    }

    // Extraire le nom du fournisseur (première ligne non vide souvent)
    const lines = text.split('\n').filter(l => l.trim().length > 0);
    if (lines.length > 0) {
      // Prendre une des premières lignes qui n'est pas juste des chiffres
      for (let line of lines.slice(0, 5)) {
        if (!/^\d+$/.test(line.trim()) && line.trim().length > 3) {
          updates.supplierName = line.trim().substring(0, 100);
          break;
        }
      }
    }

    // Détecter la catégorie par mots-clés
    const textLower = text.toLowerCase();
    if (textLower.includes('carburant') || textLower.includes('essence') || textLower.includes('diesel')) {
      updates.category = 'Frais véhicule';
    } else if (textLower.includes('restaurant') || textLower.includes('repas')) {
      updates.category = 'Repas/Restaurant';
    } else if (textLower.includes('hôtel') || textLower.includes('hotel')) {
      updates.category = 'Hébergement';
    } else if (textLower.includes('internet') || textLower.includes('télécom') || textLower.includes('mobile')) {
      updates.category = 'Télécom/Internet';
    } else if (textLower.includes('fourniture') || textLower.includes('bureau')) {
      updates.category = 'Fournitures bureau';
    } else if (textLower.includes('ordinateur') || textLower.includes('informatique')) {
      updates.category = 'Matériel informatique';
    }

    setFormData(updates);
  };

  const parseDate = (dateStr) => {
    try {
      const parts = dateStr.split(/[-/]/);
      let year, month, day;

      if (parts[0].length === 4) {
        // Format YYYY-MM-DD
        [year, month, day] = parts;
      } else {
        // Format DD/MM/YYYY
        [day, month, year] = parts;
      }

      const date = new Date(year, month - 1, day);
      if (date.getFullYear() > 2000 && date.getFullYear() < 2100) {
        return date.toISOString().split('T')[0];
      }
    } catch (err) {
      return null;
    }
    return null;
  };

  const handleFormChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    setError(null);
  };

  const handleSubmit = async () => {
    if (!file) {
      setError('Veuillez sélectionner un fichier');
      return;
    }

    if (!formData.supplierName || !formData.category || !formData.amount) {
      setError('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      setUploading(true);
      setError(null);

      const data = new FormData();
      data.append('file', file);
      Object.keys(formData).forEach((key) => {
        data.append(key, formData[key]);
      });

      await externalDocumentsService.upload(data);

      setSuccess(true);
      setTimeout(() => {
        onSuccess();
        handleClose();
      }, 1500);
    } catch (err) {
      console.error('Erreur upload:', err);
      setError(err.response?.data?.error || 'Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setFormData({
      supplierName: '',
      category: '',
      description: '',
      amount: '',
      vatRate: '20',
      documentDate: new Date().toISOString().split('T')[0],
      dueDate: '',
      paymentMethod: 'Virement',
      isPaid: false,
      paymentDate: '',
      tags: '',
      notes: '',
    });
    setError(null);
    setSuccess(false);
    onClose();
  };

  const removeFile = () => {
    setFile(null);
    setError(null);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Upload Facture Fournisseur</Typography>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {success ? (
          <Box textAlign="center" py={4}>
            <CheckCircle color="success" sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h6" color="success.main">
              Document uploadé avec succès !
            </Typography>
          </Box>
        ) : (
          <Box>
            {/* Zone de drop */}
            <Paper
              elevation={0}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              sx={{
                border: 2,
                borderStyle: 'dashed',
                borderColor: dragActive ? 'primary.main' : 'grey.300',
                bgcolor: dragActive ? 'primary.light' : 'grey.50',
                p: 4,
                mb: 3,
                cursor: 'pointer',
                transition: 'all 0.3s',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'primary.light',
                },
              }}
            >
              <input
                type="file"
                id="file-upload"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleChange}
                style={{ display: 'none' }}
              />
              <label htmlFor="file-upload" style={{ cursor: 'pointer', width: '100%' }}>
                <Box textAlign="center">
                  {file ? (
                    <Box>
                      <InsertDriveFile color="primary" sx={{ fontSize: 48, mb: 1 }} />
                      <Typography variant="body1" fontWeight="bold">
                        {file.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </Typography>
                      <Box mt={1}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={(e) => {
                            e.preventDefault();
                            removeFile();
                          }}
                        >
                          Changer de fichier
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    <Box>
                      <CloudUpload color="action" sx={{ fontSize: 48, mb: 1 }} />
                      <Typography variant="body1" fontWeight="bold" gutterBottom>
                        Glissez-déposez votre fichier ici
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        ou cliquez pour sélectionner
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                        PDF, JPG, PNG (max 10 MB)
                      </Typography>
                    </Box>
                  )}
                </Box>
              </label>
            </Paper>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {/* Formulaire */}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="Nom du fournisseur"
                  value={formData.supplierName}
                  onChange={(e) => handleFormChange('supplierName', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Catégorie</InputLabel>
                  <Select
                    value={formData.category}
                    onChange={(e) => handleFormChange('category', e.target.value)}
                    label="Catégorie"
                  >
                    {CATEGORIES.map((cat) => (
                      <MenuItem key={cat} value={cat}>
                        {cat}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  type="number"
                  label="Montant HT (€)"
                  value={formData.amount}
                  onChange={(e) => handleFormChange('amount', e.target.value)}
                  inputProps={{ step: '0.01', min: '0' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Taux TVA (%)"
                  value={formData.vatRate}
                  onChange={(e) => handleFormChange('vatRate', e.target.value)}
                  inputProps={{ step: '0.1', min: '0', max: '100' }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Date du document"
                  value={formData.documentDate}
                  onChange={(e) => handleFormChange('documentDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Date d'échéance"
                  value={formData.dueDate}
                  onChange={(e) => handleFormChange('dueDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Moyen de paiement</InputLabel>
                  <Select
                    value={formData.paymentMethod}
                    onChange={(e) => handleFormChange('paymentMethod', e.target.value)}
                    label="Moyen de paiement"
                  >
                    {PAYMENT_METHODS.map((method) => (
                      <MenuItem key={method} value={method}>
                        {method}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isPaid}
                      onChange={(e) => handleFormChange('isPaid', e.target.checked)}
                    />
                  }
                  label="Payé"
                />
                {formData.isPaid && (
                  <TextField
                    fullWidth
                    type="date"
                    size="small"
                    label="Date de paiement"
                    value={formData.paymentDate}
                    onChange={(e) => handleFormChange('paymentDate', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ mt: 1 }}
                  />
                )}
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Description"
                  value={formData.description}
                  onChange={(e) => handleFormChange('description', e.target.value)}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Tags (séparés par des virgules)"
                  value={formData.tags}
                  onChange={(e) => handleFormChange('tags', e.target.value)}
                  placeholder="ex: déductible, urgent, comptabilité"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Notes"
                  value={formData.notes}
                  onChange={(e) => handleFormChange('notes', e.target.value)}
                />
              </Grid>
            </Grid>

            {processing && (
              <Box sx={{ mt: 2 }}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <AutoFixHigh color="primary" />
                  <Typography variant="body2" color="primary">
                    Extraction automatique des données... {ocrProgress}%
                  </Typography>
                </Box>
                <LinearProgress variant="determinate" value={ocrProgress} />
              </Box>
            )}

            {uploading && <LinearProgress sx={{ mt: 2 }} />}
          </Box>
        )}
      </DialogContent>

      {!success && (
        <DialogActions>
          <Button onClick={handleClose} disabled={uploading || processing}>
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={uploading || processing || !file}
            startIcon={<CloudUpload />}
          >
            {uploading ? 'Upload en cours...' : 'Uploader'}
          </Button>
        </DialogActions>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
}

export default UploadDocumentDialog;
